/**
 * Geometry of the Saideline logo mark (the dot-matrix player), read off the logo file dot for dot.
 * The sidekick is this mark: its moods only move, dim or add dots, never redraw the player.
 */

/** `#` is a dot. Rows 0–9 are the motion lines above the player, rows 10–26 the player. */
const LOGO_MAP: string[] = [
    '............#.............',
    '............##............',
    '..............##..........',
    '.............###..........',
    '...........##.............',
    '.....................###..',
    '...................##.....',
    '.................##.....##',
    '..........................',
    '..........######..........',
    '.##...##..................',
    '####.#####................',
    '############....##........',
    '##################........',
    '.#################........',
    '..#################.......',
    '.....##########...........',
    '.....############.........',
    '.....########.............',
    '...#..##########..........',
    '..###...#######...........',
    '..###.....######..........',
    '...###....................',
    '....###...................',
    '.....###..................',
    '.......#..................',
    '........#.................',
];

/** Dot spacing and size of the logo, in the 520 × 520 disc the Figma component uses. */
export const SIDEKICK_GEOMETRY = {
    DISC: 520,
    /** Where the mark sits in the disc. */
    OFFSET_X: 79,
    OFFSET_Y: 61,
    PITCH_X: 13.85,
    PITCH_Y: 14.72,
    RADIUS: 7.5,
    LAST_MOTION_ROW: 9,
} as const;

export interface ISidekickDot {
    cx: number;
    cy: number;
    /** Part of the motion lines (true) or the player (false). */
    motion: boolean;
    /** Alternating dots of the motion lines flicker in the idle loop. */
    flicker: boolean;
    /** Start offset of the intro, where the dot flies in from. */
    introX: number;
    introY: number;
}

const { PITCH_X, PITCH_Y, RADIUS, LAST_MOTION_ROW } = SIDEKICK_GEOMETRY;
const centre = (row: number, col: number): { cx: number; cy: number } => ({
    cx: col * PITCH_X + RADIUS,
    cy: row * PITCH_Y + RADIUS,
});

/** Repeatable pseudo-random numbers, so the intro scatters the dots the same way every time. */
function scatter(seed: number): () => number {
    let state = seed;
    return () => {
        state = (state * 1103515245 + 12345) % 2147483648;
        return state / 2147483648;
    };
}

const cells: [number, number][] = [];
LOGO_MAP.forEach((line, row) => {
    for (let col = 0; col < line.length; col++) {
        if (line[col] === '#') {
            cells.push([row, col]);
        }
    }
});
const occupied = new Set(cells.map(([row, col]) => `${row},${col}`));
const random = scatter(7);

/** The 175 dots of the logo. */
export const SIDEKICK_DOTS: ISidekickDot[] = cells.map(([row, col]) => ({
    ...centre(row, col),
    motion: row <= LAST_MOTION_ROW,
    flicker: row <= LAST_MOTION_ROW && (row + col) % 2 === 1,
    introX: (random() * 2 - 1) * 170,
    introY: (random() * 2 - 1) * 170,
}));

export interface ISidekickTrailDot {
    cx: number;
    cy: number;
    /** 1 right behind the line, 2 further out (fainter). */
    step: 1 | 2;
}

/** Afterglow dots that extend the motion lines while the sidekick talks. */
export const SIDEKICK_TRAIL: ISidekickTrailDot[] = cells
    .filter(([row, col]) => row <= LAST_MOTION_ROW && !occupied.has(`${row},${col + 1}`))
    .flatMap(([row, col]) => {
        const trail: ISidekickTrailDot[] = [{ ...centre(row, col + 1), step: 1 }];
        if (!occupied.has(`${row},${col + 2}`)) {
            trail.push({ ...centre(row, col + 2), step: 2 });
        }
        return trail;
    });

/** Sparks that come off the player when it cheers (row, column on the logo grid). */
export const SIDEKICK_SPARKS = [
    [-3, 18],
    [-2, 21],
    [1, 24],
    [-3, 6],
    [0, 3],
    [4, 24],
].map(([row, col]) => centre(row, col));
